import { isObject } from '@aviata/type-guards';
import dayjs from 'dayjs';
import type { RouteState } from 'shared';

import { isRoundTripAsTwoStoredLegs } from '@/shared/lib';

import type { RecentSearch, RecentSearchTripPattern } from './types';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_RECENT_SEARCHES = 3;

const TRIP_PATTERNS = new Set<RecentSearchTripPattern>([
  'oneWay',
  'roundTrip',
  'multiCity',
  'roundTripMultiLeg',
]);

const isOptionalTripPattern = (v: unknown): boolean =>
  v === undefined ||
  (typeof v === 'string' && TRIP_PATTERNS.has(v as RecentSearchTripPattern));

const isRecentSearch = (candidate: unknown): candidate is RecentSearch =>
  isObject(candidate) &&
  Array.isArray(candidate.routes) &&
  isObject(candidate.passengers) &&
  typeof candidate.cabinClass === 'string' &&
  typeof candidate.isYouth === 'boolean' &&
  typeof candidate.timestamp === 'number' &&
  isOptionalTripPattern(
    'tripPattern' in candidate ? candidate.tripPattern : undefined,
  );

const isRecentSearchesArray = (
  candidate: unknown,
): candidate is RecentSearch[] =>
  Array.isArray(candidate) && candidate.every(isRecentSearch);

const isSameRoutes = (
  firstRoutes: RecentSearch['routes'],
  secondRoutes: RecentSearch['routes'],
): boolean => {
  if (firstRoutes.length !== secondRoutes.length) {
    return false;
  }
  return firstRoutes.every((firstLegRoute, legIndex) => {
    const secondLegRoute = secondRoutes[legIndex];
    if (!firstLegRoute || !secondLegRoute) {
      return false;
    }
    if (
      firstLegRoute.dep?.code !== secondLegRoute.dep?.code ||
      firstLegRoute.arr?.code !== secondLegRoute.arr?.code
    ) {
      return false;
    }
    const datesFromFirst = firstLegRoute.dates ?? [];
    const datesFromSecond = secondLegRoute.dates ?? [];
    if (datesFromFirst.length !== datesFromSecond.length) {
      return false;
    }
    return datesFromFirst.every(
      (dateFromFirst, dateIndex) =>
        dateFromFirst === datesFromSecond[dateIndex],
    );
  });
};

const isSameSearch = (
  firstSearch: RecentSearch,
  secondSearch: RecentSearch,
): boolean => {
  if (!firstSearch.routes.length || !secondSearch.routes.length) {
    return false;
  }

  const samePassengers =
    firstSearch.passengers.adt === secondSearch.passengers.adt &&
    firstSearch.passengers.chd === secondSearch.passengers.chd &&
    firstSearch.passengers.inf === secondSearch.passengers.inf &&
    firstSearch.passengers.yth === secondSearch.passengers.yth;

  const sameCabinClass = firstSearch.cabinClass === secondSearch.cabinClass;
  const sameYouth = firstSearch.isYouth === secondSearch.isYouth;

  return (
    isSameRoutes(firstSearch.routes, secondSearch.routes) &&
    samePassengers &&
    sameCabinClass &&
    sameYouth
  );
};

const isDepartureDatePast = (search: RecentSearch): boolean => {
  const lastRoute = search.routes[search.routes.length - 1];
  // For round-trip the inbound date lives at index 1; for multi-trip each leg
  // has a single date, so we look at the last leg's only date. Either way,
  // expire the card only after the last known travel date is in the past.
  const lastDate =
    lastRoute?.dates?.[lastRoute.dates.length - 1] ?? lastRoute?.dates?.[0];
  if (!lastDate) {
    return false;
  }
  return dayjs().isAfter(dayjs(lastDate), 'day');
};

const filterExpired = (searches: RecentSearch[]): RecentSearch[] => {
  const now = Date.now();
  return searches.filter(
    search =>
      now - search.timestamp < THIRTY_DAYS_MS && !isDepartureDatePast(search),
  );
};

const sortByTimestamp = (searches: RecentSearch[]): RecentSearch[] =>
  [...searches].sort((left, right) => right.timestamp - left.timestamp);

const limitSearches = (searches: RecentSearch[]): RecentSearch[] =>
  searches.slice(0, MAX_RECENT_SEARCHES);

/**
 * Derives trip pattern from stored routes (also used as fallback when
 * `tripPattern` is missing on legacy recent-search rows).
 */
const deriveRecentSearchTripPattern = (
  routes: RouteState[],
): RecentSearchTripPattern => {
  if (routes.length === 1) {
    const dates = routes[0]?.dates ?? [];
    const significantDateCount = dates.filter(Boolean).length;
    return significantDateCount > 1 ? 'roundTrip' : 'oneWay';
  }
  if (isRoundTripAsTwoStoredLegs(routes)) {
    return 'roundTripMultiLeg';
  }
  return 'multiCity';
};

export const recentSearchesLib = {
  isRecentSearchesArray,
  isSameSearch,
  filterExpired,
  sortByTimestamp,
  limitSearches,
  deriveRecentSearchTripPattern,
};
